import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Button,
  ButtonGroup,
  Tab,
  Tabs,
  Box,
  Card,
  CardContent,
  Divider,
  useMediaQuery,
  useTheme,
  Fade,
  Slide,
  Grow,
  Chip,
  IconButton,
  Avatar,
  LinearProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Person as PersonIcon,
  Article as ArticleIcon,
  Description as DescriptionIcon,
  School as SchoolIcon,
  Science as ScienceIcon,
  AutoStories as AutoStoriesIcon,
  Assessment as AssessmentIcon,
  Chat as ChatIcon,
  Lightbulb as LightbulbIcon,
  Summarize as SummarizeIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  Share as ShareIcon,
  Bookmark as BookmarkIcon,
  Close,
  Visibility,
  FormatQuote as FormatQuoteIcon,
  MenuBook as MenuBookIcon,
  Assignment as AssignmentIcon,
  Calculate as CalculateIcon,
  Psychology as PsychologyIcon,
  AccountTree as AccountTreeIcon,
  LibraryBooks as LibraryBooksIcon,
  CheckBox as CheckBoxIcon,
  Tune as TuneIcon,
  NoteAdd as NoteAddIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { APICategoriesList, APIResearcherList } from '../../utilities/APIS';
import { getIdCategory, getIdFullName } from '../../utilities/Functions';
import NavigationMenu from '../navigation/NavigationMenu';
import { styled } from '@mui/material/styles';
import { BASE_URL, headers } from '../../utilities/Env';
import useCustomToast from '../../hooks/useToast';
import { TEXT_CONNECTION_ERROR } from '../../utilities/Text';

const ModernPaper = styled(Paper)(({ theme }) => ({
  transition: theme.transitions.create(['box-shadow', 'transform', 'background-color'], {
    duration: theme.transitions.duration.standard,
  }),
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[8],
  },
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  overflow: 'hidden',
  '&:hover': {
    boxShadow: theme.shadows[4],
    borderColor: theme.palette.primary.light,
  },
}));

const SectionHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.primary.main + '08',
  borderBottom: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  flexWrap: 'wrap',
}));

const SectionContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  backgroundColor: theme.palette.background.paper,
}));

const WordCountBadge = styled(Chip)(({ theme, isover }) => ({
  backgroundColor: isover === 'true' ? theme.palette.error.main : theme.palette.success.main,
  color: 'white',
  fontSize: '0.75rem',
  height: 24,
  marginLeft: 'auto',
  '& .MuiChip-label': {
    padding: '0 8px',
  },
}));

const PDFViewer = ({ open, onClose, pdfUrl, title = "PDF Document" }) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ '& .MuiDialog-paper': { minHeight: '80vh' } }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">{title}</Typography>
          <IconButton onClick={onClose}>
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0, minHeight: '70vh' }}>
        {pdfUrl && (
          <iframe
            src={pdfUrl}
            width="100%"
            height="100%"
            style={{ border: 'none', minHeight: '70vh' }}
            title={title}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const ResearchDetails = () => {
  const [researchersData, setResearchersData] = useState([]);
  const [areasData, setAreasData] = useState([]);
  const [bookmarked, setBookmarked] = useState(false);
  const showToast = useCustomToast();
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [pdfDialogOpen, setPdfDialogOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState('');

  const research = location.state || {};

  // Extract all research fields from location state
  const {
    // Basic Information
    id,
    researchTopic,
    researcherName,
    researchArea,
    researchStatus,
    researchType,
    fileName,
    fileSize,
    uploadDate,
    universityName,
    pdfPlainText,
    
    // New Fields from Migration
    abstract,
    acronyms,
    definition_of_terms,
    introduction,
    objectives,
    hypothesis,
    justification,
    significance,
    theoretical_framework,
    literature_review,
    methodology,
    results_findings,
    discussion_of_results,
    conclusions_recommendations,
    references,
    appendices,
    
    // View-specific fields
    viewDate,
    viewedBy
  } = research;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [reseRes, catsRes] = await Promise.all([
        axios.get(APIResearcherList, { headers }),
        axios.get(APICategoriesList, { headers }),
      ]);

      const researchers = Array.isArray(reseRes.data.data) ? reseRes.data.data.reverse() : [];
      setResearchersData(researchers);

      const cats = Array.isArray(catsRes.data.data) ? catsRes.data.data.reverse() : [];
      setAreasData(cats);

    } catch (error) {
      let errorMessage = "Failed to load data";
      if (error.response) {
        errorMessage = error.response.data.message || error.message;
      } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
      }
      setError(errorMessage);
      showToast("Error", errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleViewPdf = () => {
    if (fileName) {
      const pdfUrl = `${BASE_URL}${fileName}`;
      setSelectedPdfUrl(pdfUrl);
      setPdfDialogOpen(true);
    }
  };

  const handleClosePdfDialog = () => {
    setPdfDialogOpen(false);
    setSelectedPdfUrl('');
  };

  const handleBookmark = () => {
    setBookmarked(!bookmarked);
    showToast("Success", bookmarked ? "Removed from bookmarks" : "Added to bookmarks", "success");
  };

  const handleShare = () => {
    showToast("Info", "Share functionality coming soon", "info");
  };

  const handleDownload = () => {
    showToast("Info", "Download functionality coming soon", "info");
  };

  const calculateWordCount = (text) => {
    if (!text) return 0;
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const renderSection = (title, content, icon, maxWords = null, required = false, tooltip = "") => {
    if (!content || content.trim().length === 0) return null;
    
    const wordCount = calculateWordCount(content);
    const isOverLimit = maxWords && wordCount > maxWords;
    const percentage = maxWords ? Math.min((wordCount / maxWords) * 100, 100) : 0;
    
    return (
      <SectionCard>
        <SectionHeader>
          <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 32, height: 32 }}>
            {icon}
          </Avatar>
          <Typography variant="h6" fontWeight="600" color="primary">
            {title}
            {required && <Chip label="Required" size="small" color="error" sx={{ ml: 1 }} />}
          </Typography>
          {maxWords && (
            <WordCountBadge
              label={`${wordCount} / ${maxWords} words`}
              isover={(wordCount > maxWords).toString()}
              size="small"
            />
          )}
          {tooltip && (
            <Tooltip title={tooltip}>
              <InfoIcon color="action" sx={{ fontSize: 18, ml: 1 }} />
            </Tooltip>
          )}
        </SectionHeader>
        <SectionContent>
          {maxWords && (
            <Box sx={{ mb: 2 }}>
              <LinearProgress 
                variant="determinate" 
                value={percentage} 
                color={isOverLimit ? "error" : percentage > 90 ? "warning" : "success"}
                sx={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}
          <Typography 
            variant="body1" 
            sx={{ 
              whiteSpace: 'pre-wrap',
              lineHeight: 1.8,
              color: isOverLimit ? theme.palette.error.main : theme.palette.text.primary
            }}
          >
            {content}
          </Typography>
          {isOverLimit && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This section exceeds the recommended word limit of {maxWords} words by {wordCount - maxWords} words.
            </Alert>
          )}
        </SectionContent>
      </SectionCard>
    );
  };

  const renderOverviewTab = () => (
    <Box>
      {/* Research Header Card */}
      <ModernPaper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ textAlign: 'center' }}>
              <Avatar sx={{ width: 80, height: 80, mx: 'auto', mb: 2, bgcolor: theme.palette.primary.main }}>
                <PersonIcon sx={{ fontSize: 40 }} />
              </Avatar>
              <Typography variant="h5" fontWeight="600" gutterBottom>
                {getIdFullName(researcherName, researchersData)}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Principal Researcher
              </Typography>
              {uploadDate && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                  Uploaded: {uploadDate}
                </Typography>
              )}
              {viewDate && (
                <Chip 
                  icon={<Visibility />}
                  label={`Viewed on ${viewDate}`}
                  size="small"
                  color="info"
                  sx={{ mt: 1 }}
                />
              )}
              {viewedBy && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  Viewed by: Researcher #{viewedBy}
                </Typography>
              )}
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined" sx={{ p: 3, height: '100%', borderRadius: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom fontWeight="600">
                Research Details
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                <Chip 
                  icon={<SchoolIcon />}
                  label={`University: ${getIdCategory(universityName, researchersData)}`}
                  color="primary" 
                  variant="outlined"
                  size="medium"
                />
                <Chip 
                  icon={<AutoStoriesIcon />}
                  label={`Area: ${getIdCategory(researchArea, areasData)}`} 
                  color="secondary" 
                  variant="outlined"
                  size="medium"
                />
                <Chip 
                  icon={<CheckCircleIcon />}
                  label={`Status: ${researchStatus}`} 
                  color={researchStatus === "Completed" ? "success" : researchStatus === "Published" ? "info" : "warning"} 
                  size="medium"
                />
                <Chip 
                  icon={<DescriptionIcon />}
                  label={`Type: ${researchType || 'Research Paper'}`} 
                  variant="outlined"
                  size="medium"
                />
                {fileSize && (
                  <Chip 
                    icon={<AttachFileIcon />}
                    label={`File Size: ${(fileSize / 1024 / 1024).toFixed(2)} MB`} 
                    variant="outlined"
                    size="small"
                  />
                )}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </ModernPaper>

      {/* Research Topic Section */}
      {renderSection('Research Topic', researchTopic, <ArticleIcon />, 20, true, "Maximum 20 words")}
      
      {/* Abstract Section */}
      {renderSection('Abstract', abstract, <SummarizeIcon />, 300, true, "Provide a concise summary of your research (max 300 words)")}
      
      {/* Justification (Internal Only) */}
      {justification && renderSection('Justification (Internal)', justification, <InfoIcon />, 1000, false, "For administrative use only")}
      
      {/* Significance (Internal Only) */}
      {significance && renderSection('Significance (Internal)', significance, <LightbulbIcon />, 1000, false, "For administrative use only")}
    </Box>
  );

  const renderFullContentTab = () => (
    <Box>
      {/* Acronyms */}
      {renderSection('Acronyms', acronyms, <FormatQuoteIcon />, 1000, false, "List of acronyms used in the research")}
      
      {/* Definition of Terms */}
      {renderSection('Definition of Terms', definition_of_terms, <MenuBookIcon />, 200, false, "Define key terms used in your research")}
      
      {/* Introduction */}
      {renderSection('Introduction', introduction, <DescriptionIcon />, 500, false, "Background and introduction to your research topic")}
      
      {/* Objectives */}
      {renderSection('Objectives', objectives, <AssignmentIcon />, 100, true, "State the main objectives of your research")}
      
      {/* Hypothesis */}
      {renderSection('Hypothesis', hypothesis, <ScienceIcon />, 100, false, "State your research hypothesis")}
      
      {/* Theoretical Framework */}
      {renderSection('Theoretical Framework', theoretical_framework, <AccountTreeIcon />, 500, false, "Theoretical foundation of your research")}
      
      {/* Literature Review */}
      {renderSection('Literature Review', literature_review, <LibraryBooksIcon />, 2000, false, "Review of relevant literature")}
      
      {/* Methodology */}
      {renderSection('Methodology', methodology, <TuneIcon />, 5000, true, "Research methodology and approach")}
      
      {/* Results / Findings */}
      {renderSection('Results / Findings', results_findings, <CalculateIcon />, 5000, true, "Present your research findings")}
      
      {/* Discussion of Results */}
      {renderSection('Discussion of Results', discussion_of_results, <PsychologyIcon />, 2000, false, "Discuss and interpret your findings")}
      
      {/* Conclusions & Recommendations */}
      {renderSection('Conclusions & Recommendations', conclusions_recommendations, <CheckBoxIcon />, 1000, true, "Conclusions and recommendations from your research")}
      
      {/* References */}
      {renderSection('References', references, <LibraryBooksIcon />, 1000, true, "List of references cited in your research")}
      
      {/* Appendices */}
      {renderSection('Appendices', appendices, <NoteAddIcon />, 1000, false, "Additional supporting materials")}
    </Box>
  );

  const renderAttachmentsTab = () => (
    <ModernPaper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="600">
        <AttachFileIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Research Documents
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      {fileName ? (
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ bgcolor: theme.palette.error.light, width: 48, height: 48 }}>
                <AttachFileIcon />
              </Avatar>
              <Box>
                <Typography variant="subtitle1" fontWeight="600">
                  {fileName.split('/').pop()}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Research PDF Document {fileSize && `• ${(fileSize / 1024 / 1024).toFixed(2)} MB`}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Visibility />}
              onClick={handleViewPdf}
            >
              View PDF
            </Button>
          </Box>
        </Card>
      ) : (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <AttachFileIcon sx={{ fontSize: 64, color: 'text.secondary', opacity: 0.5, mb: 2 }} />
          <Typography variant="body1" color="text.secondary">
            No attachments available
          </Typography>
        </Box>
      )}

      {/* PDF Text Preview if available */}
      {pdfPlainText && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom fontWeight="600">
            Extracted Text Preview
          </Typography>
          <Paper variant="outlined" sx={{ p: 2, maxHeight: 200, overflow: 'auto', bgcolor: 'grey.50' }}>
            <Typography variant="body2" color="text.secondary">
              {pdfPlainText.substring(0, 500)}...
            </Typography>
          </Paper>
        </Box>
      )}
    </ModernPaper>
  );

  const renderSimilaritiesTab = () => (
    <ModernPaper elevation={2} sx={{ p: 3, borderRadius: 3 }}>
      <Typography variant="h6" gutterBottom fontWeight="600">
        <WarningIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
        Similarity Analysis
      </Typography>
      <Divider sx={{ mb: 3 }} />
      <Alert severity="info" sx={{ mb: 2 }}>
        Similarity analysis will be available after processing the PDF document.
      </Alert>
      <Typography variant="body2" color="text.secondary">
        The similarity check compares your research with existing documents in our database using a 15% threshold.
        This helps ensure the originality and uniqueness of your research work.
      </Typography>
    </ModernPaper>
  );

  const tabs = [
    { label: 'Overview', icon: <PersonIcon />, content: renderOverviewTab() },
    { label: 'Full Content', icon: <ArticleIcon />, content: renderFullContentTab() },
    { label: 'Attachments', icon: <AttachFileIcon />, content: renderAttachmentsTab() },
    { label: 'Similarities', icon: <WarningIcon />, content: renderSimilaritiesTab() },
  ];

  if (loading) {
    return (
      <>
        <NavigationMenu />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', flexDirection: 'column', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">Loading research details...</Typography>
        </Box>
      </>
    );
  }

  if (error) {
    return (
      <>
        <NavigationMenu />
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
          <Alert severity="error" sx={{ maxWidth: 500 }}>
            <Typography variant="h6">Error loading data</Typography>
            <Typography variant="body2">{error}</Typography>
            <Button onClick={() => window.location.reload()} sx={{ mt: 2 }}>
              Retry
            </Button>
          </Alert>
        </Box>
      </>
    );
  }

  return (
    <div className='body-container'>
      <NavigationMenu />
      <Box sx={{ marginTop: 10 }}>
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
          {/* Header Section */}
          <Slide direction="down" in={true} timeout={500}>
            <ModernPaper 
              elevation={3} 
              sx={{ 
                p: { xs: 2, md: 4 }, 
                mb: 4, 
                borderRadius: 4,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white'
              }}
            >
              <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                <Grid item xs={12} md={8}>
                  <Typography variant="overline" sx={{ opacity: 0.8, letterSpacing: 1 }}>
                    Research Details
                  </Typography>
                  <Typography variant="h4" fontWeight="700" gutterBottom sx={{ mt: 1 }}>
                    {researchTopic || 'Untitled Research'}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
                    <Chip 
                      label={getIdCategory(researchArea, areasData)} 
                      variant="outlined" 
                      sx={{ color: 'white', borderColor: 'white' }} 
                    />
                    <Chip 
                      label={researchStatus} 
                      variant="outlined" 
                      sx={{ color: 'white', borderColor: 'white' }} 
                    />
                    {researchType && (
                      <Chip 
                        label={researchType} 
                        variant="outlined" 
                        sx={{ color: 'white', borderColor: 'white' }} 
                      />
                    )}
                  </Box>
                </Grid>
                <Grid item xs={12} md={4} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
                  <ButtonGroup variant="contained" sx={{ mb: 2 }}>
                    <Button
                      component={Link}
                      to="/researches"
                      startIcon={<ArticleIcon />}
                    >
                      Back to Researches
                    </Button>
                  </ButtonGroup>
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'flex-start', md: 'flex-end' } }}>
                    <Tooltip title={bookmarked ? "Remove from bookmarks" : "Add to bookmarks"}>
                      <IconButton onClick={handleBookmark} sx={{ color: 'white' }}>
                        <BookmarkIcon color={bookmarked ? "secondary" : "inherit"} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Share">
                      <IconButton onClick={handleShare} sx={{ color: 'white' }}>
                        <ShareIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton onClick={handleDownload} sx={{ color: 'white' }}>
                        <DownloadIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Grid>
              </Grid>
            </ModernPaper>
          </Slide>

          {/* Main Content */}
          <Grid container spacing={3}>
            {/* Sidebar Tabs - Desktop */}
            {!isMobile && (
              <Grid item xs={12} md={3}>
                <Slide direction="right" in={true} timeout={700}>
                  <ModernPaper elevation={2} sx={{ p: 2, borderRadius: 3, position: 'sticky', top: 100 }}>
                    <Tabs
                      orientation="vertical"
                      value={tabValue}
                      onChange={handleTabChange}
                      sx={{ 
                        '& .MuiTab-root': {
                          alignItems: 'flex-start',
                          textAlign: 'left',
                          py: 1.5,
                          borderRadius: 2,
                          mb: 0.5,
                          textTransform: 'none',
                          fontWeight: 500,
                          '&.Mui-selected': {
                            backgroundColor: theme.palette.primary.main,
                            color: 'white',
                            '& .MuiSvgIcon-root': {
                              color: 'white'
                            }
                          }
                        }
                      }}
                    >
                      {tabs.map((tab, index) => (
                        <Tab
                          key={index}
                          label={tab.label}
                          icon={tab.icon}
                          iconPosition="start"
                        />
                      ))}
                    </Tabs>
                  </ModernPaper>
                </Slide>
              </Grid>
            )}

            {/* Content Area */}
            <Grid item xs={12} md={isMobile ? 12 : 9}>
              {isMobile ? (
                <>
                  <Tabs
                    value={tabValue}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ mb: 3 }}
                  >
                    {tabs.map((tab, index) => (
                      <Tab key={index} label={tab.label} icon={tab.icon} />
                    ))}
                  </Tabs>
                  <Fade in={true} timeout={500}>
                    <Box>{tabs[tabValue].content}</Box>
                  </Fade>
                </>
              ) : (
                <Fade in={true} timeout={1000}>
                  <Box>{tabs[tabValue].content}</Box>
                </Fade>
              )}
            </Grid>
          </Grid>
        </Container>

        {/* PDF Viewer Dialog */}
        <PDFViewer
          open={pdfDialogOpen}
          onClose={handleClosePdfDialog}
          pdfUrl={selectedPdfUrl}
          title="Research PDF Document"
        />
      </Box>
    </div>
  );
};

export default ResearchDetails;